import type { ClientPoll } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Text, TouchableRipple } from 'react-native-paper';

import { usePollVote } from '@/features/api/hooks/grapevine-poll-events/usePollVote';
import type { PollVote } from '@/features/post-editor/types/poll';
import {
  getPollOptions,
  hasPollVotesChanged,
  hasUserVoted
} from '@/features/post-editor/utils/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    answers: { gap: 10 },
    answerTouchable: {
      borderWidth: 1,
      borderColor: theme.colors.primary,
      borderRadius: 100,
      overflow: 'hidden'
    },
    answer: {
      color: theme.colors.primary,
      padding: 10,
      paddingHorizontal: 16,
      textAlign: 'center'
    },
    sendButton: { marginTop: 8 },
    bottom: { alignItems: 'center' }
  });

type Props = {
  postId: string;
  poll: ClientPoll;
  onVote: () => void;
};

export const PollVoting: FC<Props> = ({ postId, poll, onVote }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'components.poll'
  });

  const isMultipleChoice = poll.multipleChoice;
  const numberOfOptions = poll.numberOfOptions ?? 5;

  const originalVotes = poll as PollVote;
  const [votes, setVotes] = useState<PollVote>(poll);

  const pollOptions = getPollOptions(votes);
  const hasVoted = hasUserVoted(originalVotes);
  const hasVotesChanged = hasPollVotesChanged(originalVotes, votes);

  const handleVote = useCallback(
    (optionIndex: number) => {
      const optionNumber = optionIndex + 1;
      const votedKey = `pollOption${optionNumber}Voted` as keyof PollVote;

      if (isMultipleChoice) {
        setVotes((prev) => ({
          ...prev,
          [votedKey]: !prev[votedKey]
        }));
      } else {
        setVotes((prev) => {
          const isVoted = prev[votedKey];
          const newVotes = { ...prev };

          if (isVoted) {
            for (let i = 1; i <= numberOfOptions; i++) {
              const key = `pollOption${i}Voted` as keyof PollVote;
              newVotes[key] = false;
            }
          } else {
            for (let i = 1; i <= numberOfOptions; i++) {
              const key = `pollOption${i}Voted` as keyof PollVote;
              newVotes[key] = i === optionNumber;
            }
          }
          return newVotes;
        });
      }
    },
    [isMultipleChoice, numberOfOptions]
  );

  const { mutate: vote, isPending } = usePollVote();

  const handleSendVote = useCallback(() => {
    vote(
      { postId, ...votes },
      {
        onSuccess: () => {
          onVote();
        }
      }
    );
  }, [onVote, postId, vote, votes]);

  return (
    <>
      <View style={styles.answers}>
        {pollOptions.map(({ optionText, voted }, index) => (
          <TouchableRipple
            key={optionText}
            accessibilityRole="button"
            style={[
              styles.answerTouchable,
              voted && { backgroundColor: theme.colors.primary }
            ]}
            onPress={() => handleVote(index)}
          >
            <Text
              variant="labelLarge"
              style={[
                styles.answer,
                voted && { color: theme.colors.onPrimary }
              ]}
            >
              {optionText}
            </Text>
          </TouchableRipple>
        ))}
      </View>

      <Button
        onPress={handleSendVote}
        mode="contained"
        buttonColor={theme.colors.error}
        style={styles.sendButton}
        loading={isPending}
        disabled={!hasVotesChanged}
      >
        {t(hasVoted ? 'change' : 'send')}
      </Button>
    </>
  );
};
